import { useEffect, useEffectEvent, useRef, useState } from "react"

import { useAgentState } from "@/agent-state"
import { useErrorToast } from "@/components/CallPanel/call-error-card"
import { formatDateDigits, getTimestamp } from "@/helpers/dateFormat"
import { getContactPhoneNumber } from "@/helpers/phoneNumber"
import { type Timer } from "@/hooks/callDurationTimer"
import { useConnectActionsHook } from "@/hooks/connectActions"
import { useCallPanelLogger } from "@/hooks/useLogger"

interface CallPanelHookType {
  campaignName: string
  displayTime: string
  phoneNumber: string | null
  shouldDisplayCounter: boolean
}

const formatDisplayTime = (timerValue: Timer): string => {
  const fHours = formatDateDigits(timerValue.hours)
  const fMinutes = formatDateDigits(timerValue.minutes)
  const fSeconds = formatDateDigits(timerValue.seconds)
  if (timerValue.hours > 0) {
    return `${fHours}:${fMinutes}:${fSeconds}`
  }

  return `${fMinutes}:${fSeconds}`
}

const useCallPanelHook = ({
  campaignName,
  phoneNumber,
}: {
  campaignName: string
  isManualCall?: boolean
  phoneNumber?: string
}): CallPanelHookType => {
  const { agentRef, state } = useAgentState()
  const agent = agentRef.current
  const { durationData } = state.context

  const [displayTime, setDisplayTime] = useState<string>("00:00")
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const { pushNetworkingError } = useErrorToast()
  const { closeContact } = useConnectActionsHook()
  const log = useCallPanelLogger()

  useEffect(() => {
    // If the call has started but not ended, update the timer
    if (durationData?.callStartTimestamp && !durationData?.callEndTimestamp) {
      const startTime = new Date(durationData.callStartTimestamp)

      const updateTimer = () => {
        const now = new Date()
        const timerValue = getTimestamp(startTime, now)
        setDisplayTime(formatDisplayTime(timerValue))
      }

      updateTimer()
      intervalRef.current = setInterval(updateTimer, 1000)
    } else {
      // If the call has ended, stop the timer
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      // double check the duraiton data is complete
      if (durationData?.callStartTimestamp && durationData?.callEndTimestamp) {
        const startTime = new Date(durationData.callStartTimestamp)
        const endTime = new Date(durationData.callEndTimestamp)
        const finalTimerValue = getTimestamp(startTime, endTime)
        setDisplayTime(formatDisplayTime(finalTimerValue))
      } else {
        setDisplayTime("00:00")
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [durationData?.callStartTimestamp, durationData?.callEndTimestamp])

  const isNetworkConnectionError = state.matches({
    busy: { error: "missed_call_agent_state" },
  })

  const onNetworkConnectionError = useEffectEvent(() => {
    pushNetworkingError("missed_call_agent_state")
    closeContact()
    log.error("Network connection error led to missed_call_agent_state.")
  })

  useEffect(() => {
    if (isNetworkConnectionError) {
      onNetworkConnectionError()
    }
  }, [isNetworkConnectionError])

  const shouldDisplayCounter = !(
    state.matches({ busy: { pre_call: "no_call" } }) ||
    state.matches({ busy: "error" })
  )

  return {
    phoneNumber: phoneNumber || getContactPhoneNumber(agent),
    shouldDisplayCounter,
    campaignName,
    displayTime,
  }
}

export { useCallPanelHook }
